import { useId } from 'react'
import { observer } from 'mobx-react-lite'
import { type BundleDialogViewModel } from './BundleDialogViewModel'
import {
  BundleDialog,
  BundleIncludedItem,
  BundleIncludedItems,
} from '@/features/admin/bundle/BundleDialog'
import { BundleFormWorkspaceDropdownContainer } from '@/features/admin/bundle/components/BundleFormWorkspaceDropdown/BundleFormWorkspaceDropdownContainer'
import { FormColumn, FormColumns, FormRow, Form } from '@/components/Form'
import { TextInputContainer } from '@/components/inputs/TextInputContainer'
import { NumericInputContainer } from '@/components/inputs/NumericInputContainer'
import { TextAreaContainer } from '@/components/inputs/TextAreaContainer'
import { type IncludedItemViewModel } from '@/features/admin/bundle/components/ManageItemsInBundleForm/IncludedItemViewModel'
import { type PriceEditorViewModel } from '@/features/admin/bundle/PriceEditorViewModel'
import { ManageItemsInBundleFormContainer } from '@/features/admin/bundle/components/ManageItemsInBundleForm/ManageItemsInBundleFormContainer'

export interface BundleDialogContainerProps {
  vm: BundleDialogViewModel
}

export const BundleDialogContainer = observer(function BundleDialogContainer({
  vm,
}: BundleDialogContainerProps) {
  const testId = 'bundle-modal'
  const formId = useId()

  return (
    <BundleDialog
      variant={vm.variant}
      showing={vm.modal.showing}
      onClose={vm.modal.close}
      isSubmitPending={vm.submit.pending}
      formId={formId}
      archivedItemsCount={vm.archivedItemsCount}
      manageItemsDialog={
        <ManageItemsInBundleFormContainer vm={vm.manageItemsInBundleVm} />
      }
      closeButtonTestId={`${testId}-close-button`}
      submitButtonTestId={`${testId}-submit-button`}
    >
      <Form id={formId} onSubmit={vm.submit}>
        <BundleFormWorkspaceDropdownContainer
          vm={vm}
          workspaceSelectorVm={vm.workspaceSelectorVm}
          isEditingBundleView={vm.variant === 'Edit'}
          testId={`${testId}-workspace`}
        />
        <FormRow>
          <TextInputContainer
            autoFocus
            label="Bundle name"
            field="name"
            model={vm}
            error={vm.validation.getError('name')}
            data-test={`${testId}-name-input`}
            help={vm.remainingCharactersIndicator.name}
          />
        </FormRow>
        <FormRow>
          <TextAreaContainer
            rows={4}
            label="Description"
            field="description"
            minLength={1}
            maxLength={280}
            help={vm.remainingCharactersIndicator.description}
            model={vm}
            error={vm.validation.getError('description')}
            data-test={`${testId}-description-input`}
          />
        </FormRow>
        <PriceInputContainer
          vm={vm.pricingVm}
          testId={`${testId}-price-input`}
        />
        <BundleIncludedItems
          includedItemsCount={vm.includedItems.length}
          onManageItems={vm.manageItemsInBundleVm.show}
          isManageItemsDisabled={!vm.manageItemsInBundleVm.isWorkspaceIdSet}
          manageItemsButtonTestId={`${testId}-manage-included-items-button`}
        >
          {vm.orderedIncludedItems.map((item) => (
            <IncludedItemContainer
              key={item.inventoryItemId}
              vm={vm}
              includedItem={item}
              testId={`${testId}-included-quantity-${item.name}`}
            />
          ))}
        </BundleIncludedItems>
      </Form>
    </BundleDialog>
  )
})

interface PriceInputContainerProps {
  vm: PriceEditorViewModel
  testId?: string
}

const PriceInputContainer = observer(function PriceInputContainer({
  vm,
  testId,
}: PriceInputContainerProps) {
  return (
    <FormRow>
      <FormColumns>
        <FormColumn>
          <NumericInputContainer
            precision={2}
            label="Client price ($)"
            field="price"
            model={vm}
            error={vm.validation.getError('price')}
            testId={testId}
            help="The amount to charge the client for all the items in the bundle."
          />
        </FormColumn>
      </FormColumns>
    </FormRow>
  )
})

interface IncludedItemContainerProps {
  vm: BundleDialogViewModel
  includedItem: IncludedItemViewModel
  testId?: string
}

const IncludedItemContainer = observer(function IncludedItemContainer({
  vm,
  includedItem,
  testId,
}: IncludedItemContainerProps) {
  return (
    <BundleIncludedItem
      variant={vm.variant}
      name={includedItem.name}
      description={includedItem.description}
      isBaseItem={includedItem.isBaseItem}
      isIncludedItem={includedItem.isIncludedItem}
      archived={includedItem.archived}
      serviceItemStatusMessage={includedItem.serviceItemStatusMessage}
      includedQuantity={includedItem.includedQuantity}
      onQuantityChange={includedItem.setQuantity}
      quantityInputTestId={testId}
    />
  )
})
